import { useState, useMemo, useEffect } from 'react';
import { DialogContent, DialogTitle, Grid, Typography, TextField, Button, Slider, MenuItem } from '@mui/material';
import { RoundedDialog, StandardDialogActions } from '../Components';
import { green, grey } from '@mui/material/colors';
import { isNumber, isArray, isFunction, isEmpty } from 'lodash';
import StateManager from '../StateManager';

const DefaultStep = 1;
const DefaultRangeMin = -5;
const DefaultRangeMax = 5;
const DefaultAcceptableMin = -2;
const DefaultAcceptableMax = 2;

export default function ColorCheckOptionsDialog({ open, onClose, onChange, initial, allFields }) {
  const [step, setStep] = useState(DefaultStep);
  const [rangeSelectType, setRangeSelectType] = useState('value');
  const [rangeMin, setRangeMin] = useState(DefaultRangeMin);
  const [rangeMinFieldId, setRangeMinFieldId] = useState('');
  const [rangeMinDataSetColumnId, setRangeMinDataSetColumnId] = useState('');
  const [rangeMax, setRangeMax] = useState(DefaultRangeMax);
  const [rangeMaxFieldId, setRangeMaxFieldId] = useState('');
  const [rangeMaxDataSetColumnId, setRangeMaxDataSetColumnId] = useState('');

  const [acceptableSelectType, setAcceptableSelectType] = useState('value');
  const [acceptableMin, setAcceptableMin] = useState(DefaultAcceptableMin);
  const [acceptableMinFieldId, setAcceptableMinFieldId] = useState('');
  const [acceptableMinDataSetColumnId, setAcceptableMinDataSetColumnId] = useState('');
  const [acceptableMax, setAcceptableMax] = useState(DefaultAcceptableMax);
  const [acceptableMaxFieldId, setAcceptableMaxFieldId] = useState('');
  const [acceptableMaxDataSetColumnId, setAcceptableMaxDataSetColumnId] = useState('');

  const numberFields = useMemo(() => {
    if (!isArray(allFields)) return [];

    const result = allFields.filter(
      (item) =>
        item.title &&
        (item.fieldType === 'number' ||
          (item.fieldType === 'dataSet' &&
            isArray(item.dataSetParams?.columns) &&
            item.dataSetParams.columns.some((x) => x.type === 'number')) ||
          item.fieldType === 'colorCheck'),
    );

    return result;
  }, [allFields]);

  useEffect(() => {
    setStep(initial?.step ?? DefaultStep);
    setRangeSelectType(initial?.rangeSelectType ?? 'value');
    setRangeMin(initial?.rangeMin ?? DefaultRangeMin);
    setRangeMinFieldId(initial?.rangeMinFieldId ?? '');
    setRangeMinDataSetColumnId(initial?.rangeMinDataSetColumnId ?? '');
    setRangeMax(initial?.rangeMax ?? DefaultRangeMax);
    setRangeMaxFieldId(initial?.rangeMaxFieldId ?? '');
    setRangeMaxDataSetColumnId(initial?.rangeMaxDataSetColumnId ?? '');
    setAcceptableSelectType(initial?.acceptableSelectType ?? 'value');
    setAcceptableMin(initial?.acceptableMin ?? DefaultAcceptableMin);
    setAcceptableMinFieldId(initial?.acceptableMinFieldId ?? '');
    setAcceptableMinDataSetColumnId(initial?.acceptableMinDataSetColumnId ?? '');
    setAcceptableMax(initial?.acceptableMax ?? DefaultAcceptableMax);
    setAcceptableMaxFieldId(initial?.acceptableMaxFieldId ?? '');
    setAcceptableMaxDataSetColumnId(initial?.acceptableMaxDataSetColumnId ?? '');
  }, [initial]);

  const buttonValues = useMemo(() => {
    if (!isNumber(rangeMin) || !isNumber(rangeMax) || !isNumber(step) || step < 0.1) {
      return [];
    }

    if (rangeMin > rangeMax) {
      return [];
    }

    const result = [];

    // calculate buttons form min to max
    let curr = rangeMin;
    while (curr <= rangeMax) {
      result.push(curr);
      curr += step;
    }

    return result;
  }, [rangeMin, rangeMax, step]);

  function done() {
    if (!isFunction(onChange)) return;

    if (step == null || step === '' || step < 0.1) {
      StateManager.setErrorAlert('Invalid step value');
    }

    if (rangeSelectType === 'field' && (!rangeMinFieldId || !rangeMaxFieldId)) {
      StateManager.setErrorAlert('Please select the range fields');
      return;
    }

    if (acceptableSelectType === 'field' && (!acceptableMinFieldId || !acceptableMaxFieldId)) {
      StateManager.setErrorAlert('Please select the passable range fields');
      return;
    }

    const result = {
      step,
      rangeSelectType,
      rangeMin: rangeSelectType === 'value' ? rangeMin : null,
      rangeMinFieldId,
      rangeMinDataSetColumnId,
      rangeMax: rangeSelectType === 'value' ? rangeMax : null,
      rangeMaxFieldId,
      rangeMaxDataSetColumnId,
      acceptableSelectType,
      acceptableMin: acceptableSelectType === 'value' ? acceptableMin : null,
      acceptableMinFieldId,
      acceptableMinDataSetColumnId,
      acceptableMax: acceptableSelectType === 'value' ? acceptableMax : null,
      acceptableMaxFieldId,
      acceptableMaxDataSetColumnId,
    };

    onChange(result);
    onClose();
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Colour check options</DialogTitle>
      <DialogContent>
        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 500 }}>
            How would you like to setup the full range of colours that can be selected?
          </Typography>
        </Grid>

        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Button
            onClick={() => setRangeSelectType('value')}
            variant={rangeSelectType === 'value' ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', borderRadius: 50 }}
          >
            Define values
          </Button>

          <Button
            onClick={() => setRangeSelectType('field')}
            variant={rangeSelectType === 'field' ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', ml: 1, borderRadius: 50 }}
          >
            Set with with fields
          </Button>
        </Grid>

        {rangeSelectType === 'value' && (
          <Grid container alignItems="center" sx={{ mt: 4 }} wrap="nowrap">
            <Slider
              min={-10}
              max={10}
              step={step}
              disableSwap
              value={[rangeMin, rangeMax]}
              valueLabelFormat={(value, index) => `${value} ${index === 0 ? '(min)' : '(max)'}`}
              valueLabelDisplay="on"
              sx={{
                '& .MuiSlider-valueLabel': (theme) => ({
                  fontSize: 14,
                  fontWeight: 'normal',
                  top: 0,
                  backgroundColor: 'unset',
                  color: theme.palette.text.primary,
                  '&::before': {
                    display: 'none',
                  },
                  '& *': {
                    background: 'transparent',
                    color: '#000',
                    ...theme.applyStyles('dark', {
                      color: '#fff',
                    }),
                  },
                }),
              }}
              onChange={(_e, value) => {
                setRangeMin(value[0]);
                setRangeMax(value[1]);
              }}
            />
          </Grid>
        )}

        {rangeSelectType === 'field' && (
          <>
            <Grid container alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="subtitle2">The lower range limit:</Typography>
            </Grid>
            <Grid container alignItems="center">
              <FieldPicker
                fieldId={rangeMinFieldId}
                dataSetColumnId={rangeMinDataSetColumnId}
                numberFields={numberFields}
                onChange={({ fieldId, dataSetColumnId }) => {
                  setRangeMinFieldId(fieldId);
                  setRangeMinDataSetColumnId(dataSetColumnId);
                }}
              />
            </Grid>

            <Grid container alignItems="center" sx={{ mt: 3 }}>
              <Typography variant="subtitle2">The upper range limit:</Typography>
            </Grid>
            <Grid container alignItems="center">
              <FieldPicker
                fieldId={rangeMaxFieldId}
                dataSetColumnId={rangeMaxDataSetColumnId}
                numberFields={numberFields}
                onChange={({ fieldId, dataSetColumnId }) => {
                  setRangeMaxFieldId(fieldId);
                  setRangeMaxDataSetColumnId(dataSetColumnId);
                }}
              />
            </Grid>
          </>
        )}

        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 500 }}>
            How would you like to setup the in specification range?
          </Typography>
        </Grid>

        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Button
            onClick={() => setAcceptableSelectType('value')}
            variant={acceptableSelectType === 'value' ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', borderRadius: 50 }}
          >
            Define values
          </Button>

          <Button
            onClick={() => setAcceptableSelectType('field')}
            variant={acceptableSelectType === 'field' ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', ml: 1, borderRadius: 50 }}
          >
            Set with fields
          </Button>
        </Grid>

        {acceptableSelectType === 'value' && (
          <Grid container alignItems="center" sx={{ mt: 4 }} wrap="nowrap">
            <Slider
              min={rangeMin}
              max={rangeMax}
              step={step}
              disableSwap
              value={[acceptableMin, acceptableMax]}
              valueLabelDisplay="on"
              valueLabelFormat={(value, index) => `${value}  ${index === 0 ? '(min)' : '(max)'}`}
              sx={{
                '& .MuiSlider-valueLabel': (theme) => ({
                  fontSize: 14,
                  fontWeight: 'normal',
                  top: 0,
                  backgroundColor: 'unset',
                  color: theme.palette.text.primary,
                  '&::before': {
                    display: 'none',
                  },
                  '& *': {
                    background: 'transparent',
                    color: '#000',
                    ...theme.applyStyles('dark', {
                      color: '#fff',
                    }),
                  },
                }),
              }}
              onChange={(_e, value) => {
                setAcceptableMin(value[0]);
                setAcceptableMax(value[1]);
              }}
            />
          </Grid>
        )}

        {acceptableSelectType === 'field' && (
          <>
            <Grid container alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="subtitle2">The lower range limit:</Typography>
            </Grid>
            <Grid container alignItems="center">
              <FieldPicker
                fieldId={acceptableMinFieldId}
                dataSetColumnId={acceptableMinDataSetColumnId}
                numberFields={numberFields}
                onChange={({ fieldId, dataSetColumnId }) => {
                  setAcceptableMinFieldId(fieldId);
                  setAcceptableMinDataSetColumnId(dataSetColumnId);
                }}
              />
            </Grid>

            <Grid container alignItems="center" sx={{ mt: 3 }}>
              <Typography variant="subtitle2">The upper range limit:</Typography>
            </Grid>
            <Grid container alignItems="center">
              <FieldPicker
                fieldId={acceptableMaxFieldId}
                dataSetColumnId={acceptableMaxDataSetColumnId}
                numberFields={numberFields}
                onChange={({ fieldId, dataSetColumnId }) => {
                  setAcceptableMaxFieldId(fieldId);
                  setAcceptableMaxDataSetColumnId(dataSetColumnId);
                }}
              />
            </Grid>
          </>
        )}

        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 500 }}>
            What should the increments be between colour options?
          </Typography>
        </Grid>

        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <TextField
            variant="standard"
            type="number"
            label="Increment value"
            required
            value={step}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : Number(e.target.value);
              if (value !== '' && value < 0) return;
              setStep(value);
            }}
            sx={{ width: 100 }}
          />
        </Grid>

        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, mr: 1 }}>
            Preview:
          </Typography>
        </Grid>
        {(rangeSelectType === 'field' || acceptableSelectType === 'field') && (
          <Grid container alignItems="center" sx={{ mt: 1 }}>
            <Typography sx={{ fontStyle: 'italic' }}>
              The exact range will be determined when filling the form out
            </Typography>
          </Grid>
        )}
        <Grid container sx={{ mt: 1, minHeight: 150 }} spacing={1} alignContent={'flex-start'}>
          {buttonValues.map((value, index) => (
            <Grid item key={index}>
              <Button
                variant={'outlined'}
                sx={{
                  borderRadius: 50,
                  borderWidth: 2,
                  ':hover': { borderWidth: 2 },
                }}
              >
                {value}
              </Button>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={done} />
    </RoundedDialog>
  );
}

function FieldPicker({ fieldId, dataSetColumnId, onChange, numberFields }) {
  const selectedDataSetNumberColumns = useMemo(() => {
    if (!fieldId || !isArray(numberFields)) return [];

    const selectedField = numberFields.find((x) => x.id === fieldId);

    if (selectedField?.fieldType !== 'dataSet' || !isArray(selectedField?.dataSetParams?.columns)) {
      return [];
    }
    const numberDataSetColumns = selectedField.dataSetParams.columns.filter((x) => x.type === 'number');
    return numberDataSetColumns;
  }, [fieldId, numberFields]);

  function sendResult(data) {
    if (!isFunction(onChange)) return;
    onChange(data);
  }

  if (!isArray(numberFields) || isEmpty(numberFields)) {
    return (
      <Grid container sx={{ py: 1 }}>
        <Typography>No fields available to choose from</Typography>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ py: 1 }}>
      <Grid container alignItems="center" wrap="nowrap">
        <TextField
          label="Pick a field"
          select
          variant="standard"
          size="small"
          sx={{ width: 180 }}
          value={fieldId}
          required
          onChange={(e) => {
            sendResult({ fieldId: e.target.value, dataSetColumnId: '' });
          }}
        >
          {isArray(numberFields) &&
            numberFields.map((columns) => (
              <MenuItem value={columns.id} key={columns.id}>
                {columns.title}
              </MenuItem>
            ))}
        </TextField>

        {!isEmpty(selectedDataSetNumberColumns) && (
          <TextField
            label="Pick a column"
            select
            variant="standard"
            size="small"
            sx={{ width: 160, ml: 1 }}
            required
            value={dataSetColumnId}
            onChange={(e) => {
              sendResult({ fieldId, dataSetColumnId: e.target.value });
            }}
          >
            {selectedDataSetNumberColumns.map((columns) => (
              <MenuItem value={columns.id} key={columns.id}>
                {columns.title}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Grid>
    </Grid>
  );
}
